import React from "react";
import TimeIndicator from "./TimeIndicator";
import TargetsList from "./TargetsList";
import FocusGuideSection from "./FocusGuideSection";
import UpcomingReminders from "./UpcomingReminders";
import ActivitiesAccordion from "./ActivitiesAccordion";
import AchievementsGallery from "./AchievementsGallery";
import QuickStats from "./QuickStats";

/**
 * DashboardContent — manages the ordering of dashboard sections.
 *
 * Priority logic:
 * - Time indicator always first.
 * - If reminders exist, they come next (highest priority).
 * - If targets exist, they come next.
 * - Focus guide + quick actions slot in after reminders/targets,
 *   but if both targets and reminders are empty, guide rises to
 *   appear directly under the time indicator.
 */
const DashboardContent = ({
  contextVariables,
  stats,
  topTargets,
  allTargets,
  upcomingReminders,
  onGuideOpen,
  onCreateTask,
}) => {
  const hasReminders = upcomingReminders.length > 0;
  const hasTargets = allTargets.length > 0;

  // If no targets and no reminders, guide goes right after time indicator
  if (!hasTargets && !hasReminders) {
    return (
      <>
        <TimeIndicator
          contextVariables={contextVariables}
          completedToday={stats.completedToday}
          totalTargets={stats.targetedTasks + stats.completedToday}
        />
        <FocusGuideSection onGuideOpen={onGuideOpen} onCreateTask={onCreateTask} />
        <ActivitiesAccordion />
        <AchievementsGallery />
        <QuickStats stats={stats} />
      </>
    );
  }

  // If targets exist but no reminders, targets first then guide
  if (hasTargets && !hasReminders) {
    return (
      <>
        <TimeIndicator
          contextVariables={contextVariables}
          completedToday={stats.completedToday}
          totalTargets={stats.targetedTasks + stats.completedToday}
        />
        <TargetsList topTargets={topTargets} allTargets={allTargets} />
        <FocusGuideSection onGuideOpen={onGuideOpen} onCreateTask={onCreateTask} />
        <ActivitiesAccordion />
        <AchievementsGallery />
        <QuickStats stats={stats} />
      </>
    );
  }

  // If reminders exist but no targets, reminders first then guide
  if (!hasTargets && hasReminders) {
    return (
      <>
        <TimeIndicator
          contextVariables={contextVariables}
          completedToday={stats.completedToday}
          totalTargets={stats.targetedTasks + stats.completedToday}
        />
        <UpcomingReminders reminders={upcomingReminders} />
        <FocusGuideSection onGuideOpen={onGuideOpen} onCreateTask={onCreateTask} />
        <ActivitiesAccordion />
        <AchievementsGallery />
        <QuickStats stats={stats} />
      </>
    );
  }

  // Both exist: reminders first (most important), then targets, then guide
  return (
    <>
      <TimeIndicator
        contextVariables={contextVariables}
        completedToday={stats.completedToday}
        totalTargets={stats.targetedTasks + stats.completedToday}
      />
      <UpcomingReminders reminders={upcomingReminders} />
      <TargetsList topTargets={topTargets} allTargets={allTargets} />
      <FocusGuideSection onGuideOpen={onGuideOpen} onCreateTask={onCreateTask} />
      <ActivitiesAccordion />
      <AchievementsGallery />
      <QuickStats stats={stats} />
    </>
  );
};

export default DashboardContent;
